// src/utils/normalizeMedium.js
// =====================================================
// Normalisation ARTKIV des médiums
//
// Doctrine :
// - medium_original       = texte brut utilisateur
// - medium_normalized_key = clé technique canonique
// - medium_family_key     = famille artistique / catégorie de comparaison
//
// Exemples :
// - "Huile sur toile"                 -> oil_on_canvas / painting
// - "Acrylique sur toile"             -> acrylic_on_canvas / painting
// - "Aquarelle"                       -> watercolor / work_on_paper
// - "Aquarelle sur papier"            -> watercolor / work_on_paper
// - "Aquarelle et mine de plomb..."   -> watercolor_and_graphite_on_paper / work_on_paper
// - "Tirage argentique"               -> silver_gelatin_print / photography
// - "Tirage lambda"                   -> lambda_print / photography
// - "C-Print"                         -> c_print / photography
// - "Mixed media on canvas"           -> mixed_media / mixed_media
// - "Art numérique"                   -> digital / digital
// - "Porcelaine émaillée"             -> porcelain / sculpture
// =====================================================

export const MEDIUM_CATEGORIES = [
  "painting",
  "photography",
  "sculpture",
  "prints",
  "drawing",
  "work_on_paper",
  "mixed_media",
  "installation",
  "film_video",
  "digital",
  "ceramics",
  "textile",
  "design",
  "other",
];

// =========================
// Nettoyage robuste
// =========================

function cleanText(value = "") {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[,;:]?\s*\b(18|19|20)\d{2}\b/g, "")
    .replace(/[’']/g, "'")
    .replace(/[–—]/g, "-")
    .replace(/\s*\/\s*/g, " / ")
    .replace(/\s*\+\s*/g, " + ")
    .replace(/\s+/g, " ")
    .trim();
}

function includesAny(normalized, terms = []) {
  return terms.some((term) => normalized.includes(cleanText(term)));
}

function hasPaper(normalized) {
  return includesAny(normalized, [
    "papier",
    "paper",
    "on paper",
    "sur papier",
    "papier encadré",
    "papier encadre",
  ]);
}

function hasCanvas(normalized) {
  return includesAny(normalized, [
    "toile",
    "canvas",
    "on canvas",
    "sur toile",
  ]);
}

// =========================
// Détection technique canonique
// Ordre volontaire :
// - cas hybrides précis d'abord
// - techniques spécifiques ensuite
// - génériques en dernier
// =========================

const CANONICAL_RULES = [
  // Hybrides précis sur papier
  {
    key: "watercolor_and_graphite_on_paper",
    category: "work_on_paper",
    technique: "aquarelle et mine de plomb sur papier",
    confidence: 0.97,
    terms: ["aquarelle", "mine de plomb"],
    requireAll: true,
  },
  {
    key: "charcoal_and_acrylic_on_paper",
    category: "work_on_paper",
    technique: "charbon et acrylique sur papier",
    confidence: 0.96,
    terms: ["charbon", "acrylique"],
    requireAll: true,
    requirePaper: true,
  },

  // Hybrides sur toile
  {
    key: "acrylic_and_graphite_on_canvas",
    category: "painting",
    technique: "acrylique et graphite sur toile",
    confidence: 0.96,
    terms: ["acrylic", "graphite"],
    requireAll: true,
    requireCanvas: true,
  },
  {
    key: "acrylic_and_spray_paint_on_canvas",
    category: "painting",
    technique: "acrylique et spray sur toile",
    confidence: 0.96,
    terms: ["acrylic", "spray"],
    requireAll: true,
    requireCanvas: true,
  },
  {
    key: "acrylic_and_ink_on_canvas",
    category: "painting",
    technique: "acrylique et encre sur toile",
    confidence: 0.95,
    terms: ["acrylique", "encre"],
    requireAll: true,
    requireCanvas: true,
  },
  {
    key: "oil_and_crayon_on_canvas",
    category: "painting",
    technique: "huile et crayon sur toile",
    confidence: 0.95,
    terms: ["oil", "crayon"],
    requireAll: true,
    requireCanvas: true,
  },
  {
    key: "oil_and_collage_on_canvas",
    category: "mixed_media",
    technique: "huile et collage sur toile",
    confidence: 0.95,
    terms: ["oil", "collage"],
    requireAll: true,
    requireCanvas: true,
  },
  {
    key: "acrylic_and_collage_on_photograph",
    category: "mixed_media",
    technique: "acrylique et collage sur photographie",
    confidence: 0.95,
    terms: ["acrylique", "collage", "photographie"],
    requireAll: true,
  },

  // Techniques mixtes parapluie
  {
    key: "mixed_media",
    category: "mixed_media",
    technique: "technique mixte",
    confidence: 0.95,
    terms: [
      "technique mixte",
      "techniques mixtes",
      "mixed media",
      "mixed-media",
      "media mixte",
      "media mixtes",
      "medias mixtes",
      "mediums mixtes",
      "médiums mixtes",
      "technique combinee",
      "techniques combinees",
      "technique combinée",
      "techniques combinées",
      "assemblage",
      "collage",
      "resine et collage",
      "résine et collage",
    ],
  },

  // Numérique
  {
    key: "digital",
    category: "digital",
    technique: "art numérique",
    confidence: 0.96,
    terms: [
      "art numerique",
      "art numérique",
      "digital art",
      "digital",
      "nft",
      "nft + impression physique",
      "impression physique",
    ],
  },

  // Peinture sur toile
  {
    key: "acrylic_on_canvas",
    category: "painting",
    technique: "acrylique sur toile",
    confidence: 0.97,
    terms: ["acrylique sur toile", "acrylic on canvas"],
  },
  {
    key: "oil_on_canvas",
    category: "painting",
    technique: "huile sur toile",
    confidence: 0.97,
    terms: ["huile sur toile", "oil on canvas"],
  },

  // Papier / dessin / aquarelle
  {
    key: "watercolor",
    category: "work_on_paper",
    technique: "aquarelle",
    confidence: 0.95,
    terms: ["aquarelle", "watercolor", "watercolour"],
  },
  {
    key: "graphite_on_paper",
    category: "work_on_paper",
    technique: "mine de plomb sur papier",
    confidence: 0.96,
    terms: ["mine de plomb", "graphite", "graphite on paper"],
  },
  {
    key: "charcoal_on_paper",
    category: "work_on_paper",
    technique: "charbon sur papier",
    confidence: 0.94,
    terms: ["charbon", "fusain", "charcoal"],
  },
  {
    key: "oil_pastel_on_paper",
    category: "work_on_paper",
    technique: "crayon à l'huile sur papier",
    confidence: 0.94,
    terms: [
      "crayon a l'huile sur papier",
      "crayon à l'huile sur papier",
      "pastel a l'huile sur papier",
      "pastel à l'huile sur papier",
      "oil pastel on paper",
    ],
  },
  {
    key: "pencil_on_paper",
    category: "work_on_paper",
    technique: "crayon sur papier",
    confidence: 0.9,
    terms: ["crayon sur papier", "pencil on paper"],
  },
  {
    key: "ink_on_paper",
    category: "work_on_paper",
    technique: "encre sur papier",
    confidence: 0.93,
    terms: ["encre sur papier", "ink on paper"],
  },

  // Prints / multiples
  {
    key: "serigraph",
    category: "prints",
    technique: "sérigraphie",
    confidence: 0.96,
    terms: [
      "serigraphie",
      "sérigraphie",
      "serigraph",
      "screenprint",
      "screen print",
      "silkscreen",
    ],
  },
  {
    key: "lithograph",
    category: "prints",
    technique: "lithographie",
    confidence: 0.94,
    terms: ["lithographie", "lithograph"],
  },
  {
    key: "etching",
    category: "prints",
    technique: "gravure / eau-forte",
    confidence: 0.9,
    terms: ["eau-forte", "eau forte", "etching", "gravure"],
  },

  // Photographie
  {
    key: "silver_gelatin_print",
    category: "photography",
    technique: "tirage argentique",
    confidence: 0.96,
    terms: [
      "tirage argentique",
      "gelatin silver print",
      "silver gelatin print",
      "silver gelatin",
      "tirage gelatino argentique",
      "tirage gélatino argentique",
    ],
  },
  {
    key: "lambda_print",
    category: "photography",
    technique: "tirage lambda",
    confidence: 0.96,
    terms: [
      "tirage lambda",
      "lambda print",
      "lambda",
      "durst lambda",
      "tirage argento numerique",
      "tirage argento-numérique",
      "argento numerique",
      "argento-numérique",
      "lightjet",
      "laserlab",
      "frontier",
    ],
  },
  {
    key: "c_print",
    category: "photography",
    technique: "c-print",
    confidence: 0.94,
    terms: [
      "c-print",
      "c print",
      "c type print",
      "type-c",
      "type c",
      "chromogenic print",
      "tirage chromogene",
      "tirage chromogène",
      "tirage chromogenique",
      "tirage chromogénique",
    ],
  },
  {
    key: "inkjet_print",
    category: "photography",
    technique: "tirage jet d'encre",
    confidence: 0.9,
    terms: [
      "jet d'encre",
      "jet d’encre",
      "inkjet",
      "pigment print",
      "archival pigment print",
      "tirage pigmentaire",
      "impression pigmentaire",
    ],
  },
  {
    key: "photography",
    category: "photography",
    technique: "photographie",
    confidence: 0.85,
    terms: ["photographie", "photography", "photograph", "photo"],
  },

  // Sculpture / objets
  {
    key: "porcelain",
    category: "sculpture",
    technique: "porcelaine",
    confidence: 0.94,
    terms: ["porcelaine", "porcelaine emaillee", "porcelaine émaillée", "porcelain"],
  },
  {
    key: "bronze_and_stainless_steel",
    category: "sculpture",
    technique: "bronze et acier inoxydable",
    confidence: 0.96,
    terms: ["bronze", "acier inoxydable"],
    requireAll: true,
  },
  {
    key: "bronze",
    category: "sculpture",
    technique: "bronze",
    confidence: 0.94,
    terms: ["bronze"],
  },
  {
    key: "steel",
    category: "sculpture",
    technique: "acier inoxydable",
    confidence: 0.9,
    terms: ["acier inoxydable", "stainless steel", "steel"],
  },
  {
    key: "resin",
    category: "sculpture",
    technique: "résine",
    confidence: 0.88,
    terms: ["resine", "résine"],
  },
  {
    key: "ceramic",
    category: "sculpture",
    technique: "céramique",
    confidence: 0.88,
    terms: ["ceramique", "céramique", "ceramic", "ceramics"],
  },
];

function ruleMatches(rule, normalized) {
  if (rule.requirePaper && !hasPaper(normalized)) return false;
  if (rule.requireCanvas && !hasCanvas(normalized)) return false;

  if (rule.requireAll) {
    return rule.terms.every((term) => normalized.includes(cleanText(term)));
  }

  return includesAny(normalized, rule.terms);
}

function getCanonicalMedium(normalized) {
  for (const rule of CANONICAL_RULES) {
    if (ruleMatches(rule, normalized)) {
      return rule;
    }
  }

  return null;
}

// =========================
// Fallback catégorie quand aucune clé technique fiable n'est trouvée
// =========================

function inferFallbackCategory(normalized) {
  if (!normalized) return null;

  if (includesAny(normalized, ["papier", "paper", "sur papier", "on paper"])) {
    return "work_on_paper";
  }

  if (includesAny(normalized, ["toile", "canvas", "huile", "oil", "acrylique", "acrylic"])) {
    return "painting";
  }

  if (includesAny(normalized, ["photo", "photographie", "photography", "tirage", "lambda", "c-print", "c print"])) {
    return "photography";
  }

  if (includesAny(normalized, ["bronze", "marbre", "sculpture", "porcelaine", "porcelain", "ceramique", "céramique", "acier", "steel"])) {
    return "sculpture";
  }

  if (includesAny(normalized, ["serigraphie", "sérigraphie", "gravure", "lithographie", "print"])) {
    return "prints";
  }

  if (includesAny(normalized, ["digital", "numerique", "numérique", "nft"])) {
    return "digital";
  }

  if (includesAny(normalized, ["mixed media", "technique mixte", "collage", "assemblage"])) {
    return "mixed_media";
  }

  return "other";
}

function inferFallbackTechnique(category) {
  switch (category) {
    case "painting":
      return "peinture";
    case "photography":
      return "photographie";
    case "sculpture":
      return "sculpture";
    case "prints":
      return "estampe";
    case "work_on_paper":
      return "travail sur papier";
    case "mixed_media":
      return "technique mixte";
    case "digital":
      return "art numérique";
    default:
      return null;
  }
}

// =========================
// Fonction principale
// =========================

export function normalizeMedium(input = "") {
  const raw = String(input || "").trim();
  const normalized = cleanText(raw);

  if (!normalized) {
    return {
      medium: null,
      medium_original: raw || null,
      technique: null,
      medium_category: null,
      confidence: 0,
      matched_term: null,
      needs_ai_review: true,
    };
  }

  const canonical = getCanonicalMedium(normalized);

  if (canonical) {
    return {
      medium: canonical.key,
      medium_original: raw,
      technique: canonical.technique,
      medium_category: canonical.category,
      confidence: canonical.confidence,
      matched_term: canonical.terms.find((term) => normalized.includes(cleanText(term))) || null,
      needs_ai_review: false,
    };
  }

  const fallbackCategory = inferFallbackCategory(normalized);

  return {
    medium: null,
    medium_original: raw,
    technique: inferFallbackTechnique(fallbackCategory),
    medium_category: fallbackCategory,
    confidence: fallbackCategory === "other" ? 0.25 : 0.55,
    matched_term: null,
    needs_ai_review: true,
  };
}

export async function normalizeMediumWithAiFallback(input, { supabase } = {}) {
  const base = normalizeMedium(input);

  if (!base.needs_ai_review || !supabase) return base;

  try {
    const { data, error } = await supabase.functions.invoke("normalize-medium", {
      body: { medium: input },
    });

    if (error || !data) return base;

    return {
      ...base,
      medium: data.medium_normalized_key || data.medium || base.medium,
      technique: data.technique || base.technique,
      medium_category: data.medium_family_key || data.medium_category || base.medium_category,
      confidence: data.confidence ?? base.confidence ?? 0.75,
      matched_term: data.matched_term || base.matched_term,
      needs_ai_review: false,
      ai_normalized: true,
    };
  } catch {
    return base;
  }
}
